import type React from 'react';
import styled from 'styled-components';

interface TooltipProps {
  text: string;
  position?:
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'distant-left';
  children: React.ReactNode;
  className?: string;
  forceHide?: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
  text,
  position = 'top',
  children,
  className,
  forceHide = false,
}) => {
  return (
    <TooltipContainer className={className}>
      {children}
      <TooltipContent position={position} forceHide={forceHide}>
        {text}
      </TooltipContent>
    </TooltipContainer>
  );
};

const TooltipContainer = styled.div`
  display: inline-flex;
  position: relative;
`;

const TooltipContent = styled.div<{
  position:
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'distant-left';
  forceHide?: boolean;
}>`
  background: var(--grey-1000);
  border-radius: 4px;
  box-shadow: var(--shadow-md);
  color: var(--white);
  font-family: var(--open-sans);
  font-size: 13px;
  font-weight: 300;
  line-height: 1;
  opacity: 0;
  padding: 10px 15px;
  position: absolute;
  text-align: center;
  transition:
    opacity 0.18s,
    transform 0.18s;
  visibility: hidden;
  white-space: nowrap;
  z-index: 1010;

  ${({ forceHide }) =>
    forceHide &&
    `
    opacity: 0 !important;
    visibility: hidden !important;
  `}

  &::after {
    border-style: solid;
    border-width: 6px;
    content: '';
    position: absolute;
  }

  ${({ position }) => {
    const positions = {
      left: {
        placement: 'right: 90%; top: 50%; margin-right: 0px;',
        transform: 'translateY(-50%) translateX(0px)',
        hoverTransform: 'translateY(-50%) translateX(0)',
        arrow:
          'left: 100%; top: 50%; transform: translateY(-50%); border-color: transparent transparent transparent var( --grey-1000);',
      },
      right: {
        placement: 'left: 110%; top: 50%; margin-left: 5px;',
        transform: 'translateY(-50%) translateX(-10px)',
        hoverTransform: 'translateY(-50%) translateX(0)',
        arrow:
          'right: 100%; top: 50%; transform: translateY(-50%); border-color: transparent var( --grey-1000) transparent transparent;',
      },
      top: {
        placement: 'bottom: 110%; left: 50%; margin-bottom: 5px;',
        transform: 'translateX(-50%) translateY(10px)',
        hoverTransform: 'translateX(-50%) translateY(0)',
        arrow:
          'top: 100%; left: 50%; transform: translateX(-50%); border-color: var( --grey-1000) transparent transparent transparent;',
      },
      bottom: {
        placement: 'top: 96%; left: 50%; margin-top: 10px;',
        transform: 'translateX(-50%) translateY(10px)',
        hoverTransform: 'translateX(-50%) translateY(0)',
        arrow:
          'bottom: 100%; left: 50%; transform: translateX(-50%); border-color: transparent transparent var( --grey-1000) transparent;',
      },
      'top-left': {
        placement: 'bottom: 110%; left: 0; margin-bottom: 5px;',
        transform: 'translateY(10px)',
        hoverTransform: 'translateY(0)',
        arrow:
          'top: 100%; left: 12px; border-color: var( --grey-1000) transparent transparent transparent;',
      },
      'top-right': {
        placement: 'bottom: 110%; right: 0; margin-bottom: 5px;',
        transform: 'translateY(10px)',
        hoverTransform: 'translateY(0)',
        arrow:
          'top: 100%; right: 12px; border-color: var( --grey-1000) transparent transparent transparent;',
      },
      'bottom-left': {
        placement: 'top: 96%; left: 0; margin-top: 10px;',
        transform: 'translateY(10px)',
        hoverTransform: 'translateY(0)',
        arrow:
          'bottom: 100%; left: 12px; border-color: transparent transparent var( --grey-1000) transparent;',
      },
      'bottom-right': {
        placement: 'top: 96%; right: 0; margin-top: 10px;',
        transform: 'translateY(10px)',
        hoverTransform: 'translateY(0)',
        arrow:
          'bottom: 100%; right: 12px; border-color: transparent transparent var( --grey-1000) transparent;',
      },
      'distant-left': {
        placement: 'right: 100%; top: 50%; margin-right: 8px;',
        transform: 'translateY(-50%) translateX(10px)',
        hoverTransform: 'translateY(-50%) translateX(0)',
        arrow:
          'left: 100%; top: 50%; transform: translateY(-50%); border-color: transparent transparent transparent var( --grey-1000);',
      },
    };

    const config = positions[position];
    return `
      ${config.placement}
      transform: ${config.transform};
      
      &::after {
        ${config.arrow}
      }
    `;
  }}

  ${TooltipContainer}:hover & {
    opacity: 1;
    transform: ${({ position }) => {
      const transforms = {
        left: 'translateY(-50%) translateX(0)',
        right: 'translateY(-50%) translateX(0)',
        top: 'translateX(-50%) translateY(0)',
        bottom: 'translateX(-50%) translateY(0)',
        'top-left': 'translateY(0)',
        'top-right': 'translateY(0)',
        'bottom-left': 'translateY(0)',
        'bottom-right': 'translateY(0)',
        'distant-left': 'translateY(-50%) translateX(0)',
      };
      return transforms[position];
    }};
    visibility: visible;
  }

  /* Override hover if forceHide is true */
  ${({ forceHide }) =>
    forceHide &&
    `
    ${TooltipContainer}:hover & {
      opacity: 0 !important;
      visibility: hidden !important;
    }
  `}
`;
